import { useState } from 'react';
import { TaskPage } from '../../../components/workbook/TaskPage';
import { TextInput } from '../../../components/workbook/forms';
import { Callout } from '../../../components/workbook/Callout';

interface DependencyItem {
  id: string;
  item: string;
  dependsOn: string;
  enables: string;
  riskIfLate: string;
  owner: string;
}

export default function DependenciesSequencingPage() {
  const [items, setItems] = useState<DependencyItem[]>([
    { id: '1', item: '', dependsOn: '', enables: '', riskIfLate: '', owner: '' },
  ]);

  const updateItem = (id: string, field: keyof DependencyItem, value: string) => {
    setItems(items.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  const addItem = () => {
    setItems([...items, { id: Date.now().toString(), item: '', dependsOn: '', enables: '', riskIfLate: '', owner: '' }]);
  };

  return (
    <TaskPage
      phase="arrange"
      title="Dependencies & Sequencing"
      pageNumber={5}
      instruction="Make constraints visible. Note: pull risk-reduction items earlier where possible."
      whyItMatters="Externalizing constraints reduces mental juggling and extraneous cognitive load."
      coachRail={{
        timing: "10 minutes",
        tips: "Focus on items that block others. Pull risk-reduction items earlier.",
        debrief: "What's the riskiest dependency? Who owns it?"
      }}
    >
      <div className="space-y-6">
        <div className="bg-[var(--surface)] p-6 overflow-x-auto">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Dependencies Grid</h3>
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-[var(--hover-tint)] border-b-2 border-[var(--rule)]">
                <th className="p-2 text-left font-semibold text-[var(--text-sec)]">Item</th>
                <th className="p-2 text-left font-semibold text-[var(--text-sec)]">Depends on</th>
                <th className="p-2 text-left font-semibold text-[var(--text-sec)]">Enables</th>
                <th className="p-2 text-left font-semibold text-[var(--text-sec)]">Risk if done late</th>
                <th className="p-2 text-left font-semibold text-[var(--text-sec)]">Owner to unblock</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-b border-[var(--rule)]">
                  <td className="p-2">
                    <TextInput
                      value={item.item}
                      onChange={(e) => updateItem(item.id, 'item', e.target.value)}
                      placeholder="Item name"
                      className="text-xs"
                    />
                  </td>
                  <td className="p-2">
                    <TextInput
                      value={item.dependsOn}
                      onChange={(e) => updateItem(item.id, 'dependsOn', e.target.value)}
                      placeholder="What must happen first?"
                      className="text-xs"
                    />
                  </td>
                  <td className="p-2">
                    <TextInput
                      value={item.enables}
                      onChange={(e) => updateItem(item.id, 'enables', e.target.value)}
                      placeholder="What does this unlock?"
                      className="text-xs"
                    />
                  </td>
                  <td className="p-2">
                    <TextInput
                      value={item.riskIfLate}
                      onChange={(e) => updateItem(item.id, 'riskIfLate', e.target.value)}
                      placeholder="What happens if delayed?"
                      className="text-xs"
                    />
                  </td>
                  <td className="p-2">
                    <TextInput
                      value={item.owner}
                      onChange={(e) => updateItem(item.id, 'owner', e.target.value)}
                      placeholder="Who owns unblocking?"
                      className="text-xs"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button
            onClick={addItem}
            className="mt-4 w-full py-2 px-4 border-2 border-dashed border-[var(--rule)] text-[var(--text-ter)] hover:border-[var(--rule)] hover:text-[var(--text-sec)] transition-colors"
          >
            + Add another item
          </button>
        </div>

        <Callout type="tip" phase="arrange">
          <p className="text-sm">Pull risk-reduction items earlier where possible. Items that block others should be prioritized.</p>
        </Callout>
      </div>
    </TaskPage>
  );
}






